import React, { useState, useEffect, useRef } from 'react';
import { Mic } from 'lucide-react';
import { Button } from './button';
import { cn } from '../../lib/utils';
import { toast } from '../../hooks/use-toast';

export function VoiceInputButton({ onTranscript, className }) {
  const [isRecording, setIsRecording] = useState(false);
  const [isSupported, setIsSupported] = useState(true);
  const recognitionRef = useRef(null);

  useEffect(() => {
    if (typeof window !== 'undefined') {
      const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
      if (!SpeechRecognition) {
        setIsSupported(false);
        return;
      }

      const recognition = new SpeechRecognition();
      recognition.continuous = false;
      recognition.lang = 'fr-FR'; // Support French by default
      recognition.interimResults = false;

      recognition.onresult = (event) => {
        const transcript = event.results[0][0].transcript;
        onTranscript(transcript);
        setIsRecording(false);
      };

      recognition.onerror = (event) => {
        console.error('Speech recognition error', event.error);
        if (event.error !== 'aborted') {
          toast.error("Erreur avec le microphone. Veuillez réessayer.");
        }
        setIsRecording(false);
      };

      recognition.onend = () => {
        setIsRecording(false);
      };

      recognitionRef.current = recognition;
    }
  }, [onTranscript]);

  const toggleRecording = () => {
    if (!isSupported) {
      toast.error("Votre navigateur ne supporte pas la saisie vocale.");
      return;
    }

    if (isRecording) {
      recognitionRef.current?.stop();
      setIsRecording(false);
    } else {
      try {
        recognitionRef.current?.start();
        setIsRecording(true);
      } catch (err) {
        console.error(err);
      }
    }
  };

  if (!isSupported) return null;

  return (
    <Button
      type="button"
      variant="outline"
      size="icon"
      className={cn(
        "transition-all duration-300",
        isRecording ? "bg-destructive/15 text-destructive hover:bg-destructive/25 border-destructive/40" : "text-muted-foreground",
        className
      )}
      onClick={toggleRecording}
      title="Saisie vocale"
    >
      {isRecording ? (
        <span className="relative flex items-center justify-center p-2">
          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-destructive opacity-75"></span>
          <Mic className="relative inline-flex size-4" />
        </span>
      ) : (
        <Mic className="size-4" />
      )}
    </Button>
  );
}
